<template>
  <div
    ref="wrapRef"
    class="min-w-0 h-full w-full rounded-2xl border border-gray-modern-100 bg-white p-6 shadow-sm"
    role="region"
    :aria-label="ariaSummary"
  >
    <div class="flex items-start justify-between gap-3">
      <h3 class="buyer-lw-donut__title min-w-0 flex-1">
        {{ title }}
      </h3>
      <ChartImageDownloadButton
        v-if="downloadable"
        :disabled="!canDownload"
        :busy="isDownloading"
        @click="downloadImage"
      />
    </div>

    <div class="buyer-lw-donut__legend mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
      <div class="flex items-center gap-2">
        <span
          class="h-3 w-3 shrink-0 rounded-full"
          :style="{ backgroundColor: colorAbove }"
          aria-hidden="true"
        />
        <span>{{ legendAbove }}</span>
      </div>
      <div class="flex items-center gap-2">
        <span
          class="h-3 w-3 shrink-0 rounded-full"
          :style="{ backgroundColor: colorBelow }"
          aria-hidden="true"
        />
        <span>{{ legendBelow }}</span>
      </div>
    </div>

    <div class="buyer-lw-donut__chart relative mx-auto mt-6">
      <svg
        class="buyer-lw-donut__chart-svg block overflow-visible"
        viewBox="-100 -100 200 200"
        aria-hidden="true"
      >
        <g v-for="(slice, i) in arcSlices" :key="i">
          <path :d="slice.d" :fill="slice.fill" stroke="white" stroke-width="3" />
        </g>
      </svg>
      <div
        class="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center"
      >
        <span class="text-3xl font-bold tabular-nums leading-none text-gray-modern-900">
          {{ pctLabel(abovePct) }}
        </span>
        <span v-if="isFinitePct(abovePct)" class="mt-1 text-sm font-medium text-gray-modern-500">
          {{ centerSubtitle }}
        </span>
      </div>
    </div>

    <p
      v-if="isFinitePct(abovePct)"
      class="buyer-lw-donut__below-line mt-4 flex flex-wrap items-baseline justify-center gap-x-1"
    >
      <span class="buyer-lw-donut__below-pct tabular-nums">{{ belowLinePctLabel }}</span>
      <span class="buyer-lw-donut__below-text">{{ belowCaptionSuffix }}</span>
    </p>

    <div class="mt-6 grid grid-cols-2 gap-3">
      <div class="buyer-lw-donut__box-above px-4 py-3">
        <p class="buyer-lw-donut__box-above-label">{{ boxWomenAbove }}</p>
        <p class="buyer-lw-donut__box-above-value tabular-nums">
          {{ pctLabel(womenAbovePct) }}
        </p>
      </div>
      <div class="buyer-lw-donut__box-above px-4 py-3">
        <p class="buyer-lw-donut__box-above-label">{{ boxMenAbove }}</p>
        <p class="buyer-lw-donut__box-above-value tabular-nums">
          {{ pctLabel(menAbovePct) }}
        </p>
      </div>
      <div class="buyer-lw-donut__box-below px-4 py-3">
        <p class="buyer-lw-donut__box-below-label">{{ boxWomenBelow }}</p>
        <p class="buyer-lw-donut__box-below-value tabular-nums">
          {{ pctLabel(womenBelowPct) }}
        </p>
      </div>
      <div class="buyer-lw-donut__box-below px-4 py-3">
        <p class="buyer-lw-donut__box-below-label">{{ boxMenBelow }}</p>
        <p class="buyer-lw-donut__box-below-value tabular-nums">
          {{ pctLabel(menBelowPct) }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import * as d3 from "d3";
import ChartImageDownloadButton from "./ChartImageDownloadButton.vue";
import { useChartImageDownload } from "../utils/useChartImageDownload";

const props = defineProps({
  downloadable: { type: Boolean, default: false },
  downloadContext: { type: String, default: "" },
  title: {
    type: String,
    default: "Share of Workers Earning a Living Wage",
  },
  legendAbove: { type: String, default: "Workers Above LW" },
  legendBelow: { type: String, default: "Workers Below LW" },
  centerSubtitle: { type: String, default: "above LW" },
  belowCaptionTemplate: {
    type: String,
    default: "%{pct}% below a Living Wage",
  },
  boxWomenAbove: { type: String, default: "Women Above LW" },
  boxMenAbove: { type: String, default: "Men Above LW" },
  boxWomenBelow: { type: String, default: "Women Below LW" },
  boxMenBelow: { type: String, default: "Men Below LW" },
  /** Portfolio share of workers at or above living wage (0–100). Null when no submitted data. */
  abovePct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
  /** Among women — % at or above LW (0–100). */
  womenAbovePct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
  /** Among men — % at or above LW (0–100). */
  menAbovePct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
  /** Among women — % below LW (0–100). */
  womenBelowPct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
  /** Among men — % below LW (0–100). */
  menBelowPct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
  /** Exact portfolio % below LW (unrounded); drives the caption under the donut. */
  belowCaptionPct: {
    default: null,
    validator: (v) => v == null || (typeof v === "number" && Number.isFinite(v)),
  },
});

const colorAbove = "var(--Brand-600, #265957)";
const colorBelow = "var(--idh-orange, #DE703E)";
const colorNoDataTrack = "var(--gray-modern-200, #E3E8EF)";

const wrapRef = ref(null);

const isFinitePct = (v) => typeof v === "number" && Number.isFinite(v);

const hasChartData = computed(() => isFinitePct(props.abovePct));

const { downloadImage, isDownloading, canDownload } = useChartImageDownload({
  getRootElement: () => wrapRef.value,
  title: () => props.title,
  downloadContext: () => props.downloadContext,
  hasData: hasChartData,
});

const pctLabel = (v) => (isFinitePct(v) ? `${Number(v).toFixed(1)}%` : "—");

/** More decimals when the share is small so "0.0%" is not misleading. */
const formatBelowCaptionPct = (v) => {
  if (!isFinitePct(v)) return "—";
  const n = Number(v);
  if (n === 0) return "0%";
  const abs = Math.abs(n);
  if (abs < 0.01) return `${n.toFixed(3)}%`;
  if (abs < 1) return `${n.toFixed(2)}%`;
  return `${n.toFixed(1)}%`;
};

const formatPct = (n) => (isFinitePct(n) ? Number(n).toFixed(1) : "—");

const belowPct = computed(() => {
  if (!isFinitePct(props.abovePct)) return null;
  const v = 100 - props.abovePct;
  return Math.min(100, Math.max(0, v));
});

const belowLinePctLabel = computed(() => {
  if (isFinitePct(props.belowCaptionPct)) return formatBelowCaptionPct(props.belowCaptionPct);
  if (isFinitePct(belowPct.value)) return pctLabel(belowPct.value);
  return "—";
});

const belowCaptionSuffix = computed(() =>
  props.belowCaptionTemplate.replace("%{pct}%", "").trimStart(),
);

const belowCaption = computed(() => {
  const rest = belowCaptionSuffix.value;
  if (!isFinitePct(props.abovePct)) {
    return rest ? rest.trim() : "";
  }
  const pct = belowLinePctLabel.value;
  return rest ? `${pct} ${rest}` : pct;
});

const ariaSummary = computed(() => {
  const head = `${props.title}: `;
  if (!isFinitePct(props.abovePct)) {
    return `${head}no submitted portfolio data.`;
  }
  return `${head}${pctLabel(props.abovePct)} ${props.centerSubtitle}; ${belowCaption.value}.`;
});

const outerRadius = 88;
const innerRadius = 56;

/** When below-LW share is > 0 but the true slice would be invisible, draw at least this arc (degrees). */
const MIN_BELOW_SLICE_DEG = 4;

const arcSlices = computed(() => {
  const arcGen = d3.arc().innerRadius(innerRadius).outerRadius(outerRadius);

  if (!isFinitePct(props.abovePct)) {
    const d = arcGen({ startAngle: 0, endAngle: 2 * Math.PI });
    return [{ d, fill: colorNoDataTrack }];
  }

  const above = Math.min(100, Math.max(0, props.abovePct));
  const below = 100 - above;

  if (above <= 0) {
    const d = arcGen({ startAngle: 0, endAngle: 2 * Math.PI });
    return [{ d, fill: colorBelow }];
  }
  if (below <= 0) {
    const d = arcGen({ startAngle: 0, endAngle: 2 * Math.PI });
    return [{ d, fill: colorAbove }];
  }

  const twoPi = 2 * Math.PI;
  let belowAngle = (below / 100) * twoPi;
  const minBelowRad = (MIN_BELOW_SLICE_DEG / 360) * twoPi;
  if (belowAngle > 0 && belowAngle < minBelowRad) {
    belowAngle = minBelowRad;
  }
  const aboveAngle = twoPi - belowAngle;

  return [
    { d: arcGen({ startAngle: 0, endAngle: aboveAngle, padAngle: 0 }), fill: colorAbove },
    {
      d: arcGen({ startAngle: aboveAngle, endAngle: twoPi, padAngle: 0 }),
      fill: colorBelow,
    },
  ];
});
</script>

<style scoped>
.buyer-lw-donut__title {
  margin: 0;
  color: #111827;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.buyer-lw-donut__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px 8px;
  color: #6b7280;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.buyer-lw-donut__legend > div {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.buyer-lw-donut__chart {
  width: 200px;
  height: 200px;
}

.buyer-lw-donut__chart-svg {
  width: 100%;
  height: 100%;
}

.buyer-lw-donut__below-line {
  margin: 0;
  font-family: "Open Sans", sans-serif;
  font-style: normal;
}

.buyer-lw-donut__below-pct {
  color: var(--idh-orange, #DE703E);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
}

.buyer-lw-donut__below-text {
  color: #6b7280;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.buyer-lw-donut__box-above {
  border-radius: 8px;
  background: var(--brand-25, #EAF7F6);
  font-family: "Open Sans", sans-serif;
}

.buyer-lw-donut__box-above-label {
  margin: 0;
  color: var(--brand-700, #174645);
  font-size: 8px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.buyer-lw-donut__box-above-value {
  margin: 8px 0 0;
  color: var(--brand-700, #174645);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.buyer-lw-donut__box-below {
  border-radius: 8px;
  background: var(--warning-50, #FFFAEB);
  font-family: "Open Sans", sans-serif;
}

.buyer-lw-donut__box-below-label {
  margin: 0;
  color: var(--warning-700, #B54708);
  font-size: 8px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.buyer-lw-donut__box-below-value {
  margin: 8px 0 0;
  color: var(--warning-700, #B54708);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}
</style>
